/**
 * The platform's tenant-facing functions, in one place.
 *
 * A tenant (customer) is sold a selection of these — one gets Etiket AI, another
 * gets quality inspection and workplace safety — and each grant carries a level:
 * `view` (they may see it) or `operate` (they may work in it). This registry is
 * the canonical list; the grants themselves are per-customer rows in
 * `customer_features` (see the migration of the same name).
 *
 * Feature keys live in CODE, not a database lookup table, for the same reason
 * the task registry does (see tasks.ts): a function is a capability of the
 * software, defined where the software is. A key here maps to the nav routes it
 * unlocks, so the tenant app can show exactly what a customer is entitled to and
 * nothing more — that gating lands in Phase 2; this file is what it will read.
 *
 * ADDING A FEATURE means an entry here (and, when its screens are built, its
 * routes). It does not mean a migration.
 */
export const FEATURES = [
    {
        key: 'live',
        label: 'Live inspection',
        summary: 'Watch verdicts as parts come off the line',
        operable: true,
        routes: ['/live'],
    },
    {
        key: 'dashboard',
        label: 'Dashboard & history',
        summary: 'Pass rate, throughput and past inspections over a time window',
        operable: false,
        routes: ['/dashboard', '/history', '/reporting'],
    },
    {
        key: 'quality',
        label: 'Quality inspection',
        summary: 'Anomaly detection — is this part like the good ones (e.g. conveyor anomalies)',
        operable: true,
        routes: ['/annotate'],
    },
    {
        key: 'labels',
        label: 'Etiket AI (label scanning)',
        summary: 'Scan a printed label and check it is correct',
        operable: true,
        routes: ['/labels'],
    },
    {
        key: 'classification',
        label: 'Classification',
        summary: 'Which product is this? — sort by class',
        operable: true,
        routes: ['/classify', '/classes', '/confusion'],
    },
    {
        key: 'safety',
        label: 'Workplace safety',
        summary: 'PPE, forklift near-miss and smoke & fire on camera frames',
        operable: true,
        routes: ['/safety'],
    },
    {
        key: 'procguard',
        label: 'Jakatori — process verification',
        summary: 'Verify each SOP step at a workstation and guide the operator on screen',
        operable: true,
        routes: ['/procguard'],
    },
];
export const FEATURE_KEYS = FEATURES.map((f) => f.key);
export function featureDef(key) {
    return FEATURES.find((f) => f.key === key);
}
/** True when a grant list permits at least `view` of the given feature. */
export function hasFeature(grants, key) {
    if (grants == null)
        return true; // null = unrestricted (staff), not "no features"
    return grants.some((g) => g.key === key);
}
/** True when a grant list permits `operate` on the given feature. */
export function canOperate(grants, key) {
    if (grants == null)
        return true;
    return grants.some((g) => g.key === key && g.level === 'operate');
}
//# sourceMappingURL=features.js.map